// ACC API client: adds the access token, parses JSON and turns failures into ApiError.
import { useCallback, useEffect, useState } from 'react';
import { config } from './config';
import { useAuth } from './auth';

export class ApiError extends Error {
  constructor(public status: number, message: string) { super(message); }
}

export interface FetchInit {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Sent as the raw request body (application/octet-stream) with its name in X-File-Name. */
  file?: File;
}
export type Fetcher = <T>(path: string, init?: FetchInit) => Promise<T>;

export function useApiClient(): Fetcher {
  const { getToken } = useAuth();
  return useCallback(async <T,>(path: string, init: FetchInit = {}) => {
    const token = await getToken();
    const headers: Record<string, string> = { authorization: `Bearer ${token}`, accept: 'application/json' };
    let body: BodyInit | undefined;
    if (init.file) {
      headers['content-type'] = 'application/octet-stream';
      headers['x-file-name'] = encodeURIComponent(init.file.name);
      body = init.file;
    } else if (init.body !== undefined) {
      headers['content-type'] = 'application/json';
      body = JSON.stringify(init.body);
    }
    const res = await fetch(`${config.apiBase}${path}`, { method: init.method ?? (body ? 'POST' : 'GET'), headers, body });
    const data = res.headers.get('content-type')?.includes('json') ? await res.json() : null;
    if (!res.ok) throw new ApiError(res.status, (data && (data as { error?: string }).error) || `Request failed (HTTP ${res.status})`);
    return data as T;
  }, [getToken]);
}

export interface Query<T> {
  data: T | null;
  error: ApiError | Error | null;
  loading: boolean;
  reload: () => void;
}

export function useQuery<T>(path: string | null): Query<T> {
  const api = useApiClient();
  const [state, setState] = useState<{ data: T | null; error: ApiError | Error | null; loading: boolean }>({ data: null, error: null, loading: Boolean(path) });
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!path) return;
    let live = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    api<T>(path).then(
      (data) => live && setState({ data, error: null, loading: false }),
      (error) => live && setState({ data: null, error, loading: false }),
    );
    return () => { live = false; };
  }, [api, path, tick]);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { ...state, reload };
}

/** Downloads an authenticated file: fetch with the token, then save through an object URL. */
export function useDownload() {
  const { getToken } = useAuth();
  return useCallback(async (path: string, fileName: string) => {
    const res = await fetch(`${config.apiBase}${path}`, { headers: { authorization: `Bearer ${await getToken()}` } });
    if (!res.ok) {
      const data = res.headers.get('content-type')?.includes('json') ? await res.json() : null;
      throw new ApiError(res.status, (data && (data as { error?: string }).error) || `Download failed (HTTP ${res.status})`);
    }
    const url = URL.createObjectURL(await res.blob());
    const link = Object.assign(document.createElement('a'), { href: url, download: fileName });
    document.body.append(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }, [getToken]);
}
